/**
 * L3 — data-confidence penalty + R/C/V tags from aggregated qaFindings.
 *
 * Penalty is ADDED on the risk/decision scale (worsens the site). Callers
 * subtract it from the benefit composite — equivalent under Azraq's protocol.
 */
import type { SiteRagReport } from '../../types/siteRag';
import type { SiteComposite } from '../../types/portfolioComposite';
import type { SiteEvidenceDocument } from '../../types/siteEvidence';
import { PORTFOLIO_CONFIG } from './portfolioConfig';
// SED D1.1 — pure, Gemini-free severity bucketer (TYPE-ONLY conflict import).
import { countUnresolvedConflictsBySeverity } from './conflictSeverityPenalty';

export type ConfidenceTag = 'V' | 'R' | 'C';

export interface DataConfidencePenalty {
  penalty: number;
  breakdown: {
    missingRequiredProviders: { count: number; penalty: number };
    missingRecommendedProviders: { count: number; penalty: number };
    danglingCitations: { count: number; penalty: number };
    provenanceGaps: { count: number; penalty: number };
    distributionOutliers: { count: number; penalty: number };
    reconciliationBlocks: { count: number; penalty: number };
    // SED D1.1 — unresolved HIGH-severity evidence conflicts (additive penalty).
    unresolvedHighConflicts: { count: number; penalty: number };
    // SED D3.2 — wide value-level uncertainty bands (DEFAULT-OFF; penalty is
    // forced to 0 unless ENABLE_D3_UNCERTAINTY_SCORING is flipped on).
    wideUncertainty: { count: number; penalty: number };
    // SED D3.3 — cross-source triangulation BONUS (DEFAULT-OFF; bonus is 0
    // unless ENABLE_D3_TRIANGULATION_SCORING is flipped on). Stored as a
    // NEGATIVE `penalty` (it offsets the raw penalty, capped, floored at 0).
    triangulationBonus: { meanAgreement: number; penalty: number };
    // Unmeasured decision-material pillar weight. `weight` is the summed
    // PILLARS weight of pillars with no source report; absent (every caller
    // that does not know the pillar set) ⇒ 0 ⇒ additive no-op.
    unmeasuredPillarWeight: { weight: number; penalty: number };
  };
  confidenceTags: ConfidenceTag[];
}

function capped(count: number, rate: number, cap: number): number {
  return Math.min(count * rate, cap);
}

/**
 * SED D3.2 — count the WIDE value-level uncertainty bands across the SED's
 * sections. A band is "wide" when its `relativeWidthPct` exceeds the
 * configured threshold. PURE: an absent / undefined `valueUncertainty`
 * contributes 0 — never fabricate uncertainty. Non-finite widths are
 * ignored (no NaN feeds the count).
 */
export function countWideUncertaintyBands(
  evidence: SiteEvidenceDocument | null | undefined,
  thresholdPct: number,
): number {
  if (!evidence) return 0;
  let n = 0;
  for (const value of Object.values(evidence as unknown as Record<string, unknown>)) {
    const section = value as { valueUncertainty?: Array<{ relativeWidthPct?: number; low?: number; high?: number; pointEstimate?: number }> } | undefined;
    const bands = section?.valueUncertainty;
    if (!Array.isArray(bands)) continue;
    for (const band of bands) {
      let widthPct = band.relativeWidthPct;
      // Derive the relative width when the producer didn't pre-compute it.
      if (widthPct == null && band.low != null && band.high != null && band.pointEstimate != null) {
        const denom = Math.abs(band.pointEstimate);
        if (denom > 0 && Number.isFinite(band.low) && Number.isFinite(band.high)) {
          widthPct = (Math.abs(band.high - band.low) / denom) * 100;
        }
      }
      if (typeof widthPct === 'number' && Number.isFinite(widthPct) && widthPct > thresholdPct) {
        n += 1;
      }
    }
  }
  return n;
}

function isDanglingCitation(kind: string): boolean {
  const k = kind.toLowerCase();
  return k.includes('dangling') || k.includes('broken') || k.includes('missing_ref');
}

function countReconciliationBlocks(report: SiteRagReport): number {
  const qa = report.qaFindings;
  if (!qa) return 0;
  let n = 0;
  for (const v of qa.internalNumberFindings ?? []) {
    if (v.severity === 'block') n += 1;
  }
  for (const v of qa.providerPrecedenceViolations ?? []) {
    if (v.severity === 'block') n += 1;
  }
  return n;
}

export function aggregateQaCounts(reports: SiteRagReport[]): {
  missingRequired: number;
  missingRecommended: number;
  danglingCitations: number;
  provenanceGaps: number;
  distributionOutliers: number;
  reconciliationBlocks: number;
} {
  let missingRequired = 0;
  let missingRecommended = 0;
  let danglingCitations = 0;
  let provenanceGaps = 0;
  let distributionOutliers = 0;
  let reconciliationBlocks = 0;

  for (const report of reports) {
    const qa = report.qaFindings;
    if (!qa) continue;
    missingRequired += qa.providerCoverage?.missingRequired?.length ?? 0;
    missingRecommended += qa.providerCoverage?.missingRecommended?.length ?? 0;
    for (const v of qa.citationIntegrityViolations ?? []) {
      if (isDanglingCitation(v.kind)) danglingCitations += 1;
    }
    for (const v of qa.docProvenanceFindings ?? []) {
      if (v.severity === 'block' || v.severity === 'warn') provenanceGaps += 1;
    }
    for (const v of qa.distribution ?? []) {
      if (v.severity === 'block' || v.severity === 'warn') distributionOutliers += 1;
    }
    reconciliationBlocks += countReconciliationBlocks(report);
  }

  return {
    missingRequired,
    missingRecommended,
    danglingCitations,
    provenanceGaps,
    distributionOutliers,
    reconciliationBlocks,
  };
}

export function assignConfidenceTag(penalty: number): ConfidenceTag {
  if (penalty < PORTFOLIO_CONFIG.DATA_CONFIDENCE_TAG_VERIFIED_MAX) return 'V';
  if (penalty <= PORTFOLIO_CONFIG.DATA_CONFIDENCE_TAG_REPORTED_MAX) return 'R';
  return 'C';
}

export function computeDataConfidencePenalty(
  siteId: string,
  reports: SiteRagReport[],
  // SED D1.1 — optional SED. When present, an UNRESOLVED HIGH-severity evidence
  // conflict adds a (capped) data-confidence penalty. Absent (every non-scorer
  // caller) ⇒ all-zero ⇒ penalty/tag byte-identical to today (additive).
  evidence?: SiteEvidenceDocument | null,
  // Summed PILLARS weight of pillars with NO source report. Supplied by
  // `computeSiteComposite`, the only caller that knows the pillar set; every
  // other caller omits it ⇒ 0 ⇒ penalty/tag byte-identical to before (additive).
  unmeasuredPillarWeight?: number,
): DataConfidencePenalty {
  void siteId;
  const counts = aggregateQaCounts(reports);

  // SED D1.1 — only HIGH-severity unresolved conflicts feed the score; low/medium
  // are surfaced elsewhere but do not move the deterministic verdict.
  const unresolvedBySeverity = countUnresolvedConflictsBySeverity(evidence?.evidenceConflicts);
  const unresolvedHighCount = unresolvedBySeverity.bySeverity.high;
  const unresolvedHighPenalty = capped(
    unresolvedHighCount,
    PORTFOLIO_CONFIG.DATA_CONFIDENCE_UNRESOLVED_HIGH_CONFLICT_RATE,
    PORTFOLIO_CONFIG.DATA_CONFIDENCE_UNRESOLVED_HIGH_CONFLICT_CAP,
  );

  const missingRequiredPenalty = capped(
    counts.missingRequired,
    PORTFOLIO_CONFIG.DATA_CONFIDENCE_MISSING_REQUIRED_RATE,
    PORTFOLIO_CONFIG.DATA_CONFIDENCE_MISSING_REQUIRED_CAP,
  );
  const missingRecommendedPenalty = capped(
    counts.missingRecommended,
    PORTFOLIO_CONFIG.DATA_CONFIDENCE_MISSING_RECOMMENDED_RATE,
    PORTFOLIO_CONFIG.DATA_CONFIDENCE_MISSING_RECOMMENDED_CAP,
  );
  const danglingPenalty = capped(
    counts.danglingCitations,
    PORTFOLIO_CONFIG.DATA_CONFIDENCE_DANGLING_CITE_RATE,
    PORTFOLIO_CONFIG.DATA_CONFIDENCE_DANGLING_CITE_CAP,
  );
  const provenancePenalty = capped(
    counts.provenanceGaps,
    PORTFOLIO_CONFIG.DATA_CONFIDENCE_PROVENANCE_GAP_RATE,
    PORTFOLIO_CONFIG.DATA_CONFIDENCE_PROVENANCE_GAP_CAP,
  );
  const distributionPenalty = capped(
    counts.distributionOutliers,
    PORTFOLIO_CONFIG.DATA_CONFIDENCE_DISTRIBUTION_OUTLIER_RATE,
    PORTFOLIO_CONFIG.DATA_CONFIDENCE_DISTRIBUTION_OUTLIER_CAP,
  );
  const reconciliationPenalty = capped(
    counts.reconciliationBlocks,
    PORTFOLIO_CONFIG.DATA_CONFIDENCE_RECONCILIATION_BLOCK_RATE,
    PORTFOLIO_CONFIG.DATA_CONFIDENCE_RECONCILIATION_BLOCK_CAP,
  );

  // ── SED D3.2 — wide value-level uncertainty (DEFAULT-OFF) ──
  // Forced to 0 (count + penalty) unless the flag is flipped on, so the
  // composite is byte-identical to today. Absent `valueUncertainty`
  // contributes 0 even when ON (never fabricate uncertainty).
  const wideUncertaintyCount = PORTFOLIO_CONFIG.ENABLE_D3_UNCERTAINTY_SCORING
    ? countWideUncertaintyBands(evidence, PORTFOLIO_CONFIG.DATA_CONFIDENCE_WIDE_UNCERTAINTY_THRESHOLD_PCT)
    : 0;
  const wideUncertaintyPenalty = PORTFOLIO_CONFIG.ENABLE_D3_UNCERTAINTY_SCORING
    ? capped(
        wideUncertaintyCount,
        PORTFOLIO_CONFIG.DATA_CONFIDENCE_WIDE_UNCERTAINTY_RATE,
        PORTFOLIO_CONFIG.DATA_CONFIDENCE_WIDE_UNCERTAINTY_CAP,
      )
    : 0;

  // ── Unmeasured decision-material pillar weight ──
  // A pillar with no source report is UNKNOWN, not good. HARD_MISSING removes
  // it from the composite denominator (correctly — an imputed worst-case fill
  // would be a fabricated NO-GO), so this term is what stops that removal from
  // making absence free: unmeasured weight costs CONFIDENCE, pushing a thin
  // site toward the 'C' tag and an honest adequacy WITHHOLD.
  // `capped` takes a COUNT × rate; here the "count" is the summed weight.
  const unmeasuredWeight =
    typeof unmeasuredPillarWeight === 'number' && Number.isFinite(unmeasuredPillarWeight)
      ? Math.max(0, unmeasuredPillarWeight)
      : 0;
  const unmeasuredPillarPenalty = capped(
    unmeasuredWeight,
    PORTFOLIO_CONFIG.DATA_CONFIDENCE_UNMEASURED_PILLAR_RATE,
    PORTFOLIO_CONFIG.DATA_CONFIDENCE_UNMEASURED_PILLAR_CAP,
  );

  const raw =
    missingRequiredPenalty +
    missingRecommendedPenalty +
    danglingPenalty +
    provenancePenalty +
    distributionPenalty +
    reconciliationPenalty +
    unresolvedHighPenalty +
    wideUncertaintyPenalty +
    unmeasuredPillarPenalty;

  // ── SED D3.3 — triangulation BONUS (DEFAULT-OFF) ──
  // Neutral (0) unless the flag is on. When on, a high mean agreement
  // (≥ MIN_AGREEMENT) earns a capped confidence lift that OFFSETS the raw
  // penalty. The offset never drives the total below 0 (a bonus cannot
  // invent negative penalty). Stored as a negative breakdown `penalty`.
  const meanAgreement = evidence?.triangulation?.meanAgreement ?? 0;
  let triangulationBonus = 0;
  if (
    PORTFOLIO_CONFIG.ENABLE_D3_TRIANGULATION_SCORING &&
    Number.isFinite(meanAgreement) &&
    meanAgreement >= PORTFOLIO_CONFIG.DATA_CONFIDENCE_TRIANGULATION_MIN_AGREEMENT
  ) {
    // Scale the bonus linearly from MIN_AGREEMENT→1.0 across [0, BONUS_MAX].
    const span = 1 - PORTFOLIO_CONFIG.DATA_CONFIDENCE_TRIANGULATION_MIN_AGREEMENT;
    const frac = span > 0 ? (meanAgreement - PORTFOLIO_CONFIG.DATA_CONFIDENCE_TRIANGULATION_MIN_AGREEMENT) / span : 1;
    triangulationBonus = Math.min(
      PORTFOLIO_CONFIG.DATA_CONFIDENCE_TRIANGULATION_BONUS_MAX,
      Math.max(0, frac) * PORTFOLIO_CONFIG.DATA_CONFIDENCE_TRIANGULATION_BONUS_MAX,
    );
  }

  const penalty = Math.max(
    0,
    Math.min(raw, PORTFOLIO_CONFIG.MAX_DATA_COMPLETENESS_PENALTY) - triangulationBonus,
  );
  const tag = assignConfidenceTag(penalty);

  return {
    penalty,
    breakdown: {
      missingRequiredProviders: { count: counts.missingRequired, penalty: missingRequiredPenalty },
      missingRecommendedProviders: { count: counts.missingRecommended, penalty: missingRecommendedPenalty },
      danglingCitations: { count: counts.danglingCitations, penalty: danglingPenalty },
      provenanceGaps: { count: counts.provenanceGaps, penalty: provenancePenalty },
      distributionOutliers: { count: counts.distributionOutliers, penalty: distributionPenalty },
      reconciliationBlocks: { count: counts.reconciliationBlocks, penalty: reconciliationPenalty },
      unresolvedHighConflicts: { count: unresolvedHighCount, penalty: unresolvedHighPenalty },
      wideUncertainty: { count: wideUncertaintyCount, penalty: wideUncertaintyPenalty },
      unmeasuredPillarWeight: { weight: unmeasuredWeight, penalty: unmeasuredPillarPenalty },
      // `|| 0` normalises -0 → 0 so the breakdown is byte-identical when the
      // bonus is zero (default-OFF).
      triangulationBonus: { meanAgreement, penalty: -triangulationBonus || 0 },
    },
    confidenceTags: [tag],
  };
}

export function toDataConfidenceBreakdown(
  penaltyResult: DataConfidencePenalty,
): SiteComposite['dataConfidenceBreakdown'] {
  const b = penaltyResult.breakdown;
  return {
    missingRequiredProviders: b.missingRequiredProviders.count,
    missingRecommendedProviders: b.missingRecommendedProviders.count,
    danglingCitations: b.danglingCitations.count,
    provenanceGaps: b.provenanceGaps.count,
    distributionOutliers: b.distributionOutliers.count,
    reconciliationBlocks: b.reconciliationBlocks.count,
    unresolvedHighConflicts: b.unresolvedHighConflicts.count,
    unmeasuredPillarWeight: b.unmeasuredPillarWeight.weight,
    penalties: {
      unmeasuredPillarWeight: b.unmeasuredPillarWeight.penalty,
      missingRequired: b.missingRequiredProviders.penalty,
      missingRecommended: b.missingRecommendedProviders.penalty,
      danglingCitations: b.danglingCitations.penalty,
      provenanceGaps: b.provenanceGaps.penalty,
      distributionOutliers: b.distributionOutliers.penalty,
      reconciliationBlocks: b.reconciliationBlocks.penalty,
      unresolvedHighConflicts: b.unresolvedHighConflicts.penalty,
    },
  };
}
